import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Globals } from 'src/app/shared/globals/globals';
import { BackendService } from 'src/app/shared/services/backend/backend.service';
import { TeiasoftLoaderService } from 'src/app/shared/services/teiasoft-bucket/teiasoft-loader.service';
import { ActiveFilter, CustomIcoCol, GridColumn, GridConfiguration } from 'src/app/shared/teiasoft-bucket/teiasoft-grid-odata/teiasoft-grid-odata.component';
import { TTButton } from 'src/app/shared/teiasoft-bucket/teiasoft-toolbar/teiasoft-toolbar.component';
import { parseGridActionEvent } from 'src/app/shared/utils/grid-event.utils';

@Component({
  selector: 'app-import-quotation',
  templateUrl: './import-quotation.component.html',
  styleUrls: ['./import-quotation.component.scss']
})
export class ImportQuotationComponent implements OnInit, OnDestroy {
  @Input() isMobile: boolean = false;
  @Input() theme: string = "silver";
  @Output() closeModal: EventEmitter<any> = new EventEmitter();
  @Output() importConfiguration: EventEmitter<any> = new EventEmitter();
  gridConfiguration: GridConfiguration;
  columnsToHide: string[] = ['Id', "DueDateColor", "UserId", "SalesOfficeId", 'CostsStatus', 'FeasibilityStatus', 'DeliveryStatus', 'QuotationExists', 'IsQuotationFinal', 'IsQuotationExternallyUploaded', 'Attachments'];
  columnsToShow: string[] = ["itemNo", "customerItemNo", "unitSystem", "sms.description", "marketFamily.name", "optionDate", "customerDeliveryDate", "applicableNorms", "remark", "normGrades.normGradeCode", "gradeCustomerAnalysisLimitation.limitation",
    "gradeCustomerOtherLimitation.limitation", "rawMaterialGrade.rawMaterialGradeCode", "npsSchedule.npsDescription", "od", "odUom", "odTolerancePlus", "odToleranceMinus", "odToleranceUom", "id", "idUom", "idTolerancePlus", "idToleranceMinus", "idToleranceUom", "wt", "wtUom", "typeOfTolerance", "tubeExecution.description", "wtTolerancePlus", "wtToleranceMinus", "wtToleranceUom",
    "quantity", "quantityUom", "lengthType", "lengthMin", "lengthMax", "lengthUom", "totalWeight", "totalWeightUom", "mill.shortName", "surfaceType.name", "endsCondition", "sql.name", "offerScheme.offerSchemeDescription", "ut", "hydroTest", "ht", "htAdditional", "inspectionType3Dot2", "multipleCutting",
    "icrIssued", "itrIssued", "idrIssued", "feasibilityStatus", "costsStatus", "deliveryStatus"];

  oDataUrl: string = "Enquiries";

  selectedEnquiry: any = null;  // Selected enquiry row
  quotationConfiguration: any = null;
  availableSections: any[] = [];  // List of sections available for import
  selectedSections: any[] = [];  // List of sections selected by the user
  subscriptions: Subscription = new Subscription();
  customIcoCols: CustomIcoCol[] = new Array();
  pdfSrc: string | null = null;  // For storing the PDF preview
  gridFilterable: boolean = false;
  buttonsList: TTButton[] = new Array();
  clearFilters: boolean = false;
  isDataFiltered: boolean = false;
  refreshGridKey: number = 0; // Key to trigger grid refresh

  step: number = 1;

  constructor(public globals: Globals, private backendService: BackendService, private teiasoftLoader: TeiasoftLoaderService, private translate: TranslateService, private sanitizer: DomSanitizer) {
    this.gridConfiguration = new GridConfiguration();
    this.gridConfiguration.defaultFilterView = 'getHavingQuotation';
    this.gridConfiguration.gridColumns = new Array();
  }

  ngOnInit(): void {
    this.buildCustomIconGrid();
    this.buildButtonsList();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  buildCustomIconGrid() {
    this.customIcoCols = new Array();

    let _item: CustomIcoCol = new CustomIcoCol();
    _item.action = "previewQuotation";
    _item.icon = ['fa-light fa-arrow-up-right-from-square'];
    _item.getTooltip = (row: any) => this.translate.instant("quotation.confirmImport");
    _item.position = "start";
    _item.disabled = false;
    _item.isMenu = false;
    this.customIcoCols.push(_item);

  }

  /**
  * TOOLBAR
  */
  buildButtonsList() {
    this.buttonsList = new Array();
    /* ALL */
    let _item: TTButton = new TTButton();

    /* SHOW/HIDE FILTER */
    _item = new TTButton();
    _item.disabled = false;
    _item.icon = !this.gridFilterable ? 'fal fa-filter' : 'fal fa-filter-circle-xmark';
    _item.id = 5;
    _item.size = "";
    _item.text = "";
    _item.tooltip = !this.gridFilterable ? "showGridFilters" : "hideGridFilters";
    _item.action = !this.gridFilterable ? "showFilter" : "closeFilter";
    _item.type = "";
    _item.state = this.isDataFiltered ? 'pressed' : '';
    this.buttonsList.push(_item);

    /* CLEAR FILTERS */
    _item = new TTButton();
    _item.disabled = !this.gridFilterable ? true : false;
    _item.icon = 'fal fa-eraser';
    _item.id = 8;
    _item.size = "";
    _item.text = "";
    _item.tooltip = "clearGridFilters";
    _item.action = "clearFilter";
    _item.type = "";
    _item.state = '';
    this.buttonsList.push(_item);


  }

  onActiveFiltersUpdate(activeFilters: ActiveFilter[]) {
    this.isDataFiltered = activeFilters.length > 0;
    this.buildButtonsList();
  }

  onBtnToolbarClick(btn: TTButton) {
    /* SHOW FILTER */
    if (btn.action == 'showFilter') {
      this.gridFilterable = true;
      this.buildButtonsList();
    }

    /* CLOSE FILTER */
    if (btn.action == 'closeFilter') {
      this.gridFilterable = false;
      this.buildButtonsList();
    }

    if (btn.action == 'clearFilter') {
      this.clearFilters = true;
      setTimeout(() => { this.clearFilters = false; this.buildButtonsList(); this.refreshFullGrid(); }, 250);
    }

  }

  refreshFullGrid() {
    this.refreshGridKey++;
  }

  getSectionList(): any[] {
    const sections = [];

    // Header section
    if (this.quotationConfiguration.headerSection) {
      sections.push({ label: 'Header', key: 'headerSection', selected: true });
    }

    if (this.quotationConfiguration.itemsSection) {
      sections.push({ label: 'Technical Descriptions', key: 'itemsSection', selected: true });
    }

    // Other sections
    if (this.quotationConfiguration.otherInformationSection) {
      sections.push({ label: 'Other Information', key: 'otherInformationSection', selected: true });
    }

    if (this.quotationConfiguration.deliverySection) {
      sections.push({ label: 'Delivery', key: 'deliverySection', selected: true });
    }

    if (this.quotationConfiguration.paymentSection) {
      sections.push({ label: 'Payment', key: 'paymentSection', selected: true });
    }

    return sections;
  }


  onSectionToggle(section: any) {

    if (section.selected) {
      if (!this.selectedSections.some(s => s.key === section.key)) {
        this.selectedSections.push(section);
      }
    } else {
      this.selectedSections = this.selectedSections.filter(s => s.key !== section.key);
    }
  }

  onImportClick() {
    const selectedConfiguration = this.selectedSections.reduce((acc, section) => {
      const [mainKey, indexOrSubKey, subKey] = section.key.split('.');
      acc[section.key] = this.quotationConfiguration[section.key];

      return acc;
    }, {});

    // Emit the selected configuration
    this.importConfiguration.emit(selectedConfiguration);
    this.closeModal.emit();
  }


  onCloseClick() {
    if (this.step == 2) {
      this.step = 1;
      return;
    }
    this.closeModal.emit('');
  }

  customIconClick($event: any) {
    const { action, row } = parseGridActionEvent($event);
    if (action === 'previewQuotation' && row) {
      this.previewQuotation(row);
    }
  }

  previewQuotation(row: any) {
    const enquiryNo = row.EnquiryNo;
    this.teiasoftLoader.start();

    setTimeout(() => {
      const pdfRequest = this.backendService.getQuotationPdf(enquiryNo);
      const sectionRequest = this.backendService.getQuotationDataAndConfiguration(enquiryNo);

      this.subscriptions.add(
        pdfRequest.subscribe({
          next: (val) => {
            const base64String = val.quotationPdf;
            const blob = this.base64ToBlob(base64String, 'application/pdf');
            const blobUrl = URL.createObjectURL(blob);
            this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl) as string;
          },
          error: () => {
            this.teiasoftLoader.stop();
          },
          complete: () => this.teiasoftLoader.stop()
        })
      );

      this.subscriptions.add(
        sectionRequest.subscribe({
          next: (config) => {
            this.quotationConfiguration = config.quotationConfiguration ? JSON.parse(config.quotationConfiguration) : null;
            this.availableSections = this.getSectionList();
            this.selectedSections = [...this.availableSections]; // Preselect all sections
          },
          complete: () => {
            this.step = 2;
          }
        })
      );
    }, 0);

  }

  base64ToBlob(base64: string, contentType: string): Blob {
    const binary = window.atob(base64);
    const array = [];
    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: contentType });
  }

  confirmPreview() {
    this.step = 3;
  }

}
